import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import {
  getInitialDateTimeRange,
  getPresetDateTimeRange,
} from "../utils/dateRange";
dayjs.extend(utc);
dayjs.extend(timezone);


export default function LoginLog() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();
  const initialRange = getInitialDateTimeRange();
  const [users, setUsers] = useState([]);
  const [logsData, setLogsData] = useState(null);
  const [filterResult, setFilterResult] = useState("");
  const [startDateTime, setStartDateTime] = useState(initialRange.start);
  const [endDateTime, setEndDateTime] = useState(initialRange.end);

  const handleDateRange = (type) => {
    const range = getPresetDateTimeRange(type);
    setStartDateTime(range.start);
    setEndDateTime(range.end);
  };

  const filteredLogs = React.useMemo(() => {
    if (filterResult === "") return logsData || [];
    // DB에 저장한 result 필드가 "성공"/"실패" 라면…
    return (logsData || []).filter((log) =>
      filterResult === "true"
        ? log.result === "성공"
        : filterResult === "false"
        ? log.result !== "성공"
        : true
    );
  }, [logsData, filterResult]);

  const sortOptions = [
    { value: "process_dt:asc", label: "처리시간 - 내림차순" },
    { value: "process_dt:desc", label: "처리시간 - 오름차순" },
  ];

  function getAgentLevel(userInfo, userId) {
    // 1) 나(self)
    if (userInfo.myId === userId) return 0;

    // 2) agents 트리에서 재귀 탐색
    function traverse(list) {
      for (const ag of list) {
        // (a) 에이전트 자신
        if (ag.userId === userId) return parseInt(ag.level, 10);

        // (c) 하위 에이전트들
        if (ag.agents?.length) {
          const lv = traverse(ag.agents);
          if (lv != null) return lv;
        }
      }
      return null;
    }

    return traverse(userInfo.agents || []) ?? null;
  }

  function getBadgeClass(level) {
    if (level === 0) return "super";
    if (level > 0) return `M${level}`;
    return ""; // 못 찾으면 기본
  }

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE}/api/Users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUsers(res.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          if (
            err.response.status === 401 ||
            err.response.status === 403 ||
            err.response.status === 500
          ) {
            localStorage.removeItem("token");
            setIsLoggedIn(false); // 로그인 상태 false 처리
            navigate("/login"); // 로그인 화면으로 리다이렉트
            return;
          }
        }
        console.error("유저 정보 불러오기 실패:", err);
      }
    };

    fetchUserInfo();
  }, []);

  const fetchLogsData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/LoginLogs`, {
        params: {
          processed_at_start: startDateTime.replace("T", " ") + ":00",
          processed_at_end: endDateTime.replace("T", " ") + ":59",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLogsData(res.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false); // 로그인 상태 false 처리
          navigate("/login"); // 로그인 화면으로 리다이렉트
          return;
        }
      }
      console.error("조회 실패:", err);
    }
  };

  const renderRow = (log, idx) => {
    if (!logsData || logsData.length === 0) {
      return (
        <tr>
          <td className="text-center span-cell-emt" colSpan="14">
            <div className="nk-order-ovwg-ck text-red">
              <h5>조회된 정보가 없습니다.</h5>
            </div>
          </td>
        </tr>
      );
    }
    return (
      <tr key={log.idx} data-pos-y={idx}>
        {/* 0번째 컬럼 (빈 셀) */}
        {/* <td data-columns="gameList-col-0" data-pos-x="0"></td> */}

        {/* 1번째 컬럼: User ID */}
        <td
          className="text-center"
          data-columns="gameList-col-1"
          data-pos-x="1"
        >
          <a href="#">
            <span
              className={`badge me-1 ${getBadgeClass(
                getAgentLevel(users, log.user_id)
              )}`}
              data-bs-original-title={log.user_id}
              data-bs-toggle="tooltip"
              data-placement="top"
            >
              {log.user_id}
            </span>
          </a>
        </td>

        {/* 2번째 컬럼: Action */}
        <td
          className="text-center"
          data-columns="gameList-col-2"
          data-pos-x="2"
        >
          <div className="btn btn-sm btn-info">{log.action}</div>
        </td>

        {/* 3번째 컬럼: Message */}
        <td
          className="text-center"
          data-columns="gameList-col-3"
          data-pos-x="3"
        >
          {log.msg}
        </td>

        {/* 4번째 컬럼: Result */}
        <td
          className="text-center"
          data-columns="gameList-col-4"
          data-pos-x="4"
        >
          <div
            className={
              log.result === "성공"
                ? "btn btn-sm btn-success"
                : "btn btn-sm btn-danger"
            }
          >
            {log.result === "성공" && <i className="fas fa-check" />}
            {log.result}
          </div>
        </td>

        {/* 5번째 컬럼: IP */}
        <td
          className="text-center"
          data-columns="gameList-col-5"
          data-pos-x="5"
        >
          {log.ip}
        </td>

        {/* 6번째 컬럼: Date */}
        <td
          className="text-center"
          data-columns="gameList-col-6"
          data-pos-x="6"
        >
          <span style={{ fontSize: "95%" }}>
            {dayjs(log.process_dt).tz("Asia/Seoul").format("YYYY-MM-DD")}
            <font className="dateInHour">
              ({dayjs(log.process_dt).tz("Asia/Seoul").format("HH:mm:ss")} UTC
              +9 )
            </font>
          </span>
        </td>
      </tr>
    );
  };

  useEffect(() => {
    if (!users) return;
    fetchLogsData();
  }, [users]);

  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block d-flex vLogsAgent">
              <div className="w-100 card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-3 p-0">
                    <form
                      className="mb-0"
                      onSubmit={(e) => {
                        e.preventDefault();
                        fetchLogsData();
                      }}
                    >
                      <DateTimeRangePicker
                        startDateTime={startDateTime}
                        endDateTime={endDateTime}
                        onStartChange={setStartDateTime}
                        onEndChange={setEndDateTime}
                        onPresetChange={handleDateRange}
                      >
                        <div className="col-12 col-sm-6 col-lg-6 col-xl-3 col-xxl-4">
                          <div className="form-control-wrap">
                            <div
                              className="input-group"
                              style={{ flexWrap: "nowrap" }}
                            >
                              <select
                                className="form-control"
                                value={filterResult}
                                onChange={(e) =>
                                  setFilterResult(e.target.value)
                                }
                              >
                                <option value="">전체</option>
                                <option value="true">성공</option>
                                <option value="false">실패</option>
                              </select>
                              <div className="input-group-append">
                                <button
                                  type="submit"
                                  className="btn btn-outline-primary btn-dim"
                                >
                                  <em className="icon ni ni-search me-2" /> 검색
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </DateTimeRangePicker>
                    </form>
                  </div>

                  <div className="card-inner tableBox px-0 pb-0">
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <div data-pattern="priority-columns">
                          <table
                            cellSpacing="0"
                            className="table table-small-font table-tighten table-bordered table-striped"
                          >
                            <thead>
                              <tr data-pos-y="0">
                                {/* <th
                                  className="text-center"
                                  data-pos-x="0"
                                  id="gameList-col-0"
                                >
                                  트리정보
                                </th> */}
                                <th
                                  className="text-center"
                                  data-pos-x="1"
                                  id="gameList-col-1"
                                >
                                  에이전트 정보
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="2"
                                  id="gameList-col-2"
                                >
                                  액션
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="3"
                                  id="gameList-col-3"
                                >
                                  메세지
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="4"
                                  id="gameList-col-4"
                                >
                                  결과
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="5"
                                  id="gameList-col-5"
                                >
                                  아이피
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="6"
                                  id="gameList-col-6"
                                >
                                  처리시간
                                </th>
                              </tr>
                            </thead>

                            {filteredLogs && (
                              <PaginatedSortableTable
                                data={filteredLogs}
                                renderRow={renderRow}
                                defaultSortField="process_dt"
                                defaultSortOrder="desc"
                                sortOptions={sortOptions}
                              />
                            )}
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
